import "./QuestionGroup.less";

import { ActionButtons, ReorderButtons } from "features/common/ActionIcons";
import { ScoreTag } from "features/common/ScoreTag";
import { LevelIndicator } from "features/qualityForm/common/LevelIndicator";
import {
    DependentIds,
    EntityType,
    initiateQuestionGroupDeletion,
    moveQuestionGroupDownIfValid,
    moveQuestionGroupUpIfValid,
    selectQuestionGroup,
    selectQuestionGroupMaxTotalScore,
    selectQuestionGroupName,
    VisibilityConflictsOnMove,
    selectCanEditForm,
    selectIsFormWeighted,
    qualityFormActions,
} from "features/qualityForm/qualityFormSlice";
import { selectForm } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { useState } from "react";
import { I18nComponent } from "react/utils/I18nComponent";

import { QuestionGroupEditor } from "./QuestionGroupEditor";
import {
    GroupScaledPointsViewer,
    GroupScoreScalingFactorEditor,
    GroupScoreScalingFactorViewer,
} from "./QuestionGroupWeights";
import { shallowEqual } from "react-redux";

import { ErrorAction } from "../common/ErrorBox/VisibilityConflictErrorBox";
import { VisibilityConflictErrorBoxContainer } from "../common/ErrorBox/VisibilityConflictErrorBoxContainer";
import { QuestionGroupSettingsIndicators } from "features/qualityForm/common/questionComponents/SettingsIndicators";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { GuxTruncate } from "genesys-spark-components-react";
import { MarkdownInline } from "features/common/Markdown";

export function QuestionGroupMetaData({
    questionGroupId,
    editQuestionGroupWeight = false,
}: {
    questionGroupId: string;
    editQuestionGroupWeight?: boolean;
}) {
    const isEditMode = useAppSelector(
        (state) =>
            state.qualityForm.data.questionGroups?.[questionGroupId]
                .showEditMode ?? false
    );

    const questionGroupTotalPoints = useAppSelector(
        (state) => selectQuestionGroupMaxTotalScore(state, questionGroupId) ?? 0
    );
    const questionCount = useAppSelector(
        (state) =>
            selectQuestionGroup(state, questionGroupId)?.questions?.length ?? 0
    );

    const isWeighted = useAppSelector(selectIsFormWeighted);

    return (
        <div className="QuestionGroupMetaData">
            {!isEditMode && (
                <QuestionGroupSettingsIndicators
                    questionGroupId={questionGroupId}
                />
            )}
            <div className="QuestionGroupMetaDataSummary">
                <span className="question-group-total-questions">
                    <I18nComponent
                        i18nKey={
                            "qualityFormEditor.questionGroup.labels.questionCount"
                        }
                        substitutions={{
                            questionCount: questionCount,
                        }}
                    />
                </span>
                <span className="question-group-total-score">
                    <I18nComponent
                        i18nKey={
                            "qualityFormEditor.questionGroup.labels.totalPoints"
                        }
                        substitutions={{
                            ScoreTag: (
                                <ScoreTag>{questionGroupTotalPoints}</ScoreTag>
                            ),
                        }}
                    />
                </span>
                {isWeighted &&
                    (!editQuestionGroupWeight ? (
                        <GroupScoreScalingFactorViewer
                            questionGroupId={questionGroupId}
                        />
                    ) : (
                        <GroupScoreScalingFactorEditor
                            questionGroupId={questionGroupId}
                        />
                    ))}
                {isWeighted && !editQuestionGroupWeight && (
                    <GroupScaledPointsViewer
                        questionGroupId={questionGroupId}
                    />
                )}
            </div>
        </div>
    );
}

export function QuestionGroupSummary({
    questionGroupId,
    onEdit,
    onDelete,
}: {
    questionGroupId: string;
    onEdit: () => void;
    onDelete?: () => void;
}) {
    const dispatch = useAppDispatch();

    const questionGroupName = useAppSelector((state) =>
        selectQuestionGroupName(state, questionGroupId)
    );
    const { canMoveUp, canMoveDown } = useAppSelector((state) => {
        const questionGroupIds = selectForm(state)?.questionGroups;
        const questionGroupPosition = (questionGroupIds ?? [])?.indexOf(
            questionGroupId
        );
        return {
            canMoveUp: questionGroupPosition > 0,
            canMoveDown:
                questionGroupPosition < (questionGroupIds ?? []).length - 1,
        };
    }, shallowEqual);
    const canEditForm = useAppSelector((state) => {
        return selectCanEditForm(state);
    });

    const [visibilityError, setVisibilityError] = useState<{
        visibilityDependentIds: VisibilityConflictsOnMove;
        errorAction: ErrorAction;
    }>();

    return (
        <VisibilityConflictErrorBoxContainer
            visibilityConflictsOnMove={visibilityError?.visibilityDependentIds}
            clearVisibilityConflicts={() => setVisibilityError(undefined)}
            entityType={EntityType.QuestionGroup}
            errorAction={visibilityError?.errorAction ?? ErrorAction.MoveUp}
        >
            <div className="QuestionGroupSummary">
                {canEditForm && (
                    <ReorderButtons
                        onMoveUp={
                            canMoveUp
                                ? () => {
                                      const visibilityDependentIds = dispatch(
                                          moveQuestionGroupUpIfValid(
                                              questionGroupId
                                          )
                                      ) as unknown as DependentIds;

                                      setVisibilityError({
                                          visibilityDependentIds,
                                          errorAction: ErrorAction.MoveUp,
                                      });
                                  }
                                : undefined
                        }
                        onMoveDown={
                            canMoveDown
                                ? () => {
                                      const visibilityDependentIds = dispatch(
                                          moveQuestionGroupDownIfValid(
                                              questionGroupId
                                          )
                                      ) as unknown as DependentIds;

                                      setVisibilityError({
                                          visibilityDependentIds,
                                          errorAction: ErrorAction.MoveDown,
                                      });
                                  }
                                : undefined
                        }
                    />
                )}
                <LevelIndicator variant="circle" />
                <div className="summary">
                    <h2 className="name gux-heading-md-semibold">
                        <GuxTruncate max-lines={3}>
                            <MarkdownInline
                                markdownInlineText={questionGroupName ?? ""}
                            ></MarkdownInline>
                        </GuxTruncate>
                    </h2>
                    <QuestionGroupMetaData questionGroupId={questionGroupId} />
                </div>
                {canEditForm && (
                    <ActionButtons onEdit={onEdit} onDelete={onDelete} />
                )}
            </div>
        </VisibilityConflictErrorBoxContainer>
    );
}

export function QuestionGroup({
    questionGroupId,
}: {
    questionGroupId: string;
}) {
    const showEditMode = useAppSelector(
        (state) =>
            state.qualityForm.data.questionGroups?.[questionGroupId]
                .showEditMode ?? false
    );
    const dispatch = useAppDispatch();
    const [visibilityDependentIds, setVisibilityDependentIds] =
        useState<DependentIds>();
    const canDeleteQuestionGroup = useAppSelector((state) => {
        return (state.qualityForm.data.form?.questionGroups?.length ?? 0) > 1;
    });
    const onDelete = () => {
        const visibilityConflicts = dispatch(
            initiateQuestionGroupDeletion(questionGroupId)
        ) as unknown as DependentIds;
        if (
            visibilityDependentIds?.questionGroupIds.length !== 0 ||
            visibilityDependentIds?.questionIds.length !== 0
        ) {
            setVisibilityDependentIds(visibilityConflicts);
        }
    };
    return (
        <VisibilityConflictErrorBoxContainer
            visibilityDependentIds={visibilityDependentIds}
            clearVisibilityConflicts={() =>
                setVisibilityDependentIds(undefined)
            }
            entityType={EntityType.QuestionGroup}
            errorAction={ErrorAction.Delete}
        >
            <div className="QuestionGroup">
                {showEditMode ? (
                    <QuestionGroupEditor
                        questionGroupId={questionGroupId}
                        onDone={() => {
                            dispatch(
                                qualityFormActions.switchEditModeForQuestionGroup(
                                    {
                                        questionGroupId,
                                        showEditMode: false,
                                    }
                                )
                            );
                        }}
                        onDelete={canDeleteQuestionGroup ? onDelete : undefined}
                    />
                ) : (
                    <QuestionGroupSummary
                        questionGroupId={questionGroupId}
                        onEdit={() =>
                            dispatch(
                                qualityFormActions.switchEditModeForQuestionGroup(
                                    {
                                        questionGroupId,
                                        showEditMode: true,
                                    }
                                )
                            )
                        }
                        onDelete={canDeleteQuestionGroup ? onDelete : undefined}
                    />
                )}
            </div>
        </VisibilityConflictErrorBoxContainer>
    );
}
